import React, { useCallback, useRef } from 'react';
import type { CustomFilterProps } from 'ag-grid-react';
import { useGridFilter } from 'ag-grid-react';
import { FILTER_STYLES } from '../AgGridCompliance.config';

type CollaborationColumnFilterModel = {
  filterType: 'collaborationColumn';
  filterValues: {
    slackProfile: string;
    teamsProfile: string;
  };
} | null;

const OPTIONS = {
  slackProfile: {
    label: 'Slack profile',
    options: [
      { value: 'all', label: 'All' },
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Missing' },
    ],
  },
  teamsProfile: {
    label: 'Teams profile',
    options: [
      { value: 'all', label: 'All' },
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Missing' },
    ],
  },
} as const;

const KEYS = ['slackProfile', 'teamsProfile'] as const;
function getValue(model: CollaborationColumnFilterModel, key: (typeof KEYS)[number]): string {
  if (!model?.filterValues) {
    return 'all';
  }
  return model.filterValues[key] || 'all';
}
export const CollaborationColumnFilter = ({
  model,
  onModelChange,
}: CustomFilterProps<any, any, CollaborationColumnFilterModel>) => {
  const refFirst = useRef<HTMLSelectElement>(null);
  //const value = model?.filterValues?.slackProfile || 'all';

  const filterValues: Record<string, string> = {};
  KEYS.forEach((key) => {
    filterValues[key] = getValue(model, key);
  });


  const doesFilterPass = useCallback(() => true, []);
  const afterGuiAttached = useCallback(() => {
    refFirst.current?.focus();
  }, []);
  useGridFilter({ doesFilterPass, afterGuiAttached });

  const updateModel = (key: string, value: string) => {
    const next = { ...filterValues, [key]: value };
    const hasActive = Object.values(next).some((v) => v !== 'all');
    if (!hasActive) {
      onModelChange(null);
      return;
    }
    onModelChange({
      filterType: 'collaborationColumn',
      filterValues: {
        slackProfile: next.slackProfile,
        teamsProfile: next.teamsProfile,
      },
    });
  };
  const handleReset = () => onModelChange(null);

  const handleChange = (key: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateModel(key, e.target.value);
  };

  const handleFocus = (e: React.FocusEvent<HTMLSelectElement | HTMLButtonElement>) => {
    e.currentTarget.style.border = FILTER_STYLES.focusBorder;
  };

  const handleBlur = (e: React.FocusEvent<HTMLSelectElement | HTMLButtonElement>) => {
    e.currentTarget.style.border = FILTER_STYLES.defaultBorder;
  };

  return (
    <div style={FILTER_STYLES.container}>
      <div style={FILTER_STYLES.section}>
        <h4 style={FILTER_STYLES.sectionHeading}>Collaboration</h4>
        {KEYS.map((key, idx) => {
          const config = OPTIONS[key];
          return (
            <div key={key} style={FILTER_STYLES.fieldGroup}>
              <label style={FILTER_STYLES.label}>{config.label}:</label>
              <select
                ref={idx === 0 ? refFirst : undefined}
                style={FILTER_STYLES.select}
                value={filterValues[key]}
                onChange={handleChange(key)}
                onFocus={handleFocus}
                onBlur={handleBlur}
              >
                {config.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
      <div style={FILTER_STYLES.resetButtonContainer}>
        <button type="button" style={FILTER_STYLES.resetButton} onClick={handleReset} onFocus={handleFocus} onBlur={handleBlur}>
          Reset All
        </button>
      </div>
    </div>
  );
};

export default CollaborationColumnFilter;
